<template>
  <section class="max-w-[1440px] mx-auto px-12 py-12 space-y-12">

    <!-- HEADER -->
    <header>
      <h2 class="text-4xl font-black tracking-tight">
        New Campaign
      </h2>
      <p class="text-slate-400 mt-2 max-w-2xl">
        Define your campaign goals and audience focus before selecting influencers.
      </p>
    </header>

    <!-- STEP INDICATOR -->
    <!-- <section class="flex items-center gap-6 text-xs font-bold uppercase tracking-wider">
      <span class="text-slate-900">1. Campaign Details</span>
      <span class="text-slate-300">→</span>
      <span class="text-slate-300">2. Influencer Fit</span>
      <span class="text-slate-300">→</span>
      <span class="text-slate-300">3. Readiness</span>
    </section> -->
    <section class="flex items-center gap-6 text-xs font-bold uppercase tracking-wider">

      <span :class="step >= 1 ? activeStep : inactiveStep">
        1. Campaign Details
      </span>

      <span class="text-slate-300">→</span>

      <span :class="step >= 2 ? activeStep : inactiveStep">
        2. Influencer Fit
      </span>

      <span class="text-slate-300">→</span>

      <span :class="step >= 3 ? activeStep : inactiveStep">
        3. Readiness
      </span>

    </section>

    <!-- FORM -->
    <section v-if="step===1" class="bg-white p-10 rounded-[2.5rem] premium-shadow
             border border-slate-50 grid grid-cols-1 lg:grid-cols-12 gap-10">

      <!-- LEFT -->
      <div class="lg:col-span-8 space-y-8">

        <!-- Campaign Name -->
        <div>
          <label class="label">Campaign Name</label>
          <input v-model="form.name" class="input" placeholder="e.g. Q3 Product Awareness" />
        </div>

        <!-- Objective -->
        <div>
          <label class="label">Campaign Objective</label>
          <select v-model="form.objective" class="input">
            <option value="">Select objective</option>
            <option>Brand Awareness</option>
            <option>Product Launch</option>
            <option>Lead Generation</option>
            <option>Community Growth</option>
          </select>
        </div>

        <!-- Description -->
        <div>
          <label class="label">Campaign Description</label>
          <textarea v-model="form.description" class="input min-h-[140px]"
            placeholder="Brief description of campaign intent (internal use only)" />
        </div>

        <!-- Timeline -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label class="label">Start Date</label>
            <input type="date" v-model="form.startDate" class="input" />
          </div>

          <div>
            <label class="label">End Date</label>
            <input type="date" v-model="form.endDate" class="input" />
          </div>
        </div>

      </div>

      <!-- RIGHT -->
      <div class="lg:col-span-4 space-y-8">

        <!-- Audience Focus -->
        <div>
          <h3 class="text-xl font-bold mb-4">
            Target Audience Focus
          </h3>

          <div class="space-y-4">
            <div>
              <label class="label">Primary Interests</label>
              <select v-model="form.interests" class="input">
                <option value="">Select interest</option>
                <option>Technology</option>
                <option>Finance</option>
                <option>Education</option>
                <option>Fitness</option>
                <option>Marketing</option>
              </select>
            </div>

            <div>
              <label class="label">Preferred Education Level</label>
              <select v-model="form.education" class="input">
                <option value="">Any</option>
                <option>Undergraduate</option>
                <option>Postgraduate</option>
                <option>High School</option>
              </select>
            </div>

            <div>
              <label class="label">Primary Region</label>
              <select v-model="form.region" class="input">
                <option value="">Any</option>
                <option>India</option>
                <option>North America</option>
                <option>Europe</option>
                <option>APAC</option>
              </select>
            </div>
          </div>
        </div>

        <!-- NOTE -->
        <div class="bg-slate-50 p-6 rounded-xl text-xs text-slate-500">
          These filters are used to evaluate influencer fit.
          They do not restrict discovery results.
        </div>

      </div>
    </section>

    <section v-if="step === 2" class="space-y-10">

      <!-- HEADER -->
      <div class="flex items-end justify-between">
        <div>
          <h3 class="text-3xl font-bold">
            Influencer Fit
          </h3>
          <p class="text-slate-400">
            Evaluate creators aligned with your campaign goals.
          </p>
        </div>


      </div>


      <!-- CREATOR GRID -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

        <div v-for="inf in influencers" :key="inf.id" class="bg-white p-6 rounded-2xl premium-shadow border border-slate-50
                  hover:shadow-xl transition-all">

          <!-- TOP -->
          <div class="flex items-center gap-4">
            <div class="size-14 rounded-full bg-slate-900 text-white
                      flex items-center justify-center font-bold">
              {{ inf.name.charAt(0) }}
            </div>

            <div>
              <p class="text-sm font-bold">
                {{ inf.name }}
              </p>
              <p class="text-xs text-slate-400">
                {{ inf.platform }} · {{ inf.category }}
              </p>
            </div>
          </div>

          <!-- KPIs -->
          <div class="grid grid-cols-2 gap-4 mt-6 text-xs">

            <div>
              <p class="text-slate-400">Verified Fans</p>
              <p class="font-bold">{{ inf.fans }}</p>
            </div>

            <div>
              <p class="text-slate-400">Trust Score</p>
              <p class="font-bold text-emerald-600">{{ inf.trust }}%</p>
            </div>

            <div>
              <p class="text-slate-400">Risk Score</p>
              <p class="font-bold text-rose-500">{{ inf.risk }}%</p>
            </div>

            <div>
              <p class="text-slate-400">Fit Score</p>
              <p class="font-bold">{{ inf.fit }}/10</p>
            </div>

          </div>

          <!-- ACTIONS -->
          <div class="flex gap-2 mt-6">

            <button class="ghost-btn flex-1" @click="toggleShortlist(inf)">
              {{inf.shortlisted?"Shortlisted":"⭐ Shortlist"}}
            </button>


          </div>

        </div>

      </div>

    </section>

    <section v-if="step === 3" class="space-y-10">

      <!-- HEADER -->
      <div>
        <h3 class="text-2xl font-black tracking-tight">
          Campaign Readiness
        </h3>
        <p class="text-slate-400 text-sm mt-1">
          Final validation before activating your campaign
        </p>
      </div>

     <div class="bg-white p-6 rounded-2xl premium-shadow border border-slate-50">

      <p class="text-[11px] font-bold uppercase tracking-wider text-slate-400">
        Campaign Configuration
      </p>

      <div class="grid grid-cols-2 gap-6 mt-4 text-sm">

        <div>
          <p class="text-slate-400 text-xs">Campaign Name</p>
          <p class="font-bold text-slate-800">{{ form.name }}</p>
        </div>

        <div>
          <p class="text-slate-400 text-xs">Objective</p>
          <p class="font-bold text-slate-800">{{ form.objective }}</p>
        </div>

        <div>
          <p class="text-slate-400 text-xs">Timeline</p>
          <p class="font-bold text-slate-800">
            {{ form.startDate }} → {{ form.endDate }}
          </p>
        </div>

        <div>
          <p class="text-slate-400 text-xs">Target Audience</p>
          <p class="font-bold text-slate-800">
            {{ form.interests || "Any" }}
          </p>
        </div>

      </div>
    </div>

      <!-- SELECTED INFLUENCERS SUMMARY -->
     <div class="grid grid-cols-1 md:grid-cols-3 gap-6">

  <div class="bg-white p-6 rounded-2xl premium-shadow border border-slate-50">
    <p class="text-[11px] font-bold uppercase tracking-wider text-slate-400">
      Selected Creators
    </p>
    <h4 class="text-3xl font-black mt-2">
      {{ selectedInfluencers.length }}
    </h4>
    <p class="text-xs text-slate-400 mt-2">
      Creators aligned with campaign filters
    </p>
  </div>

  <div class="bg-white p-6 rounded-2xl premium-shadow border border-slate-50">
    <p class="text-[11px] font-bold uppercase tracking-wider text-slate-400">
      Estimated Verified Reach
    </p>
    <h4 class="text-3xl font-black mt-2">
      {{ totalReach }}
    </h4>
    <p class="text-xs text-slate-400 mt-2">
      Derived from verified fan graphs
    </p>
  </div>

  <div class="bg-white p-6 rounded-2xl premium-shadow border border-slate-50">
    <p class="text-[11px] font-bold uppercase tracking-wider text-slate-400">
      Campaign Risk Level
    </p>
    <h4 class="text-3xl font-black mt-2 text-emerald-600">
      Low
    </h4>
    <p class="text-xs text-slate-400 mt-2">
      Based on creator risk scores
    </p>
  </div>

</div>

      <!-- SYSTEM VALIDATION -->
      <div class="bg-slate-50 rounded-xl p-5 text-xs text-slate-500 leading-relaxed">
        Campaign readiness metrics are generated using platform-owned audience data.
        No scraped data or third-party estimates influence this validation.
      </div>

     

    </section>

    <!-- ACTIONS -->
    <section class="flex justify-between items-center pt-6">
      <button v-if="step>1" @click="backNow" class="text-xs font-bold uppercase tracking-wider
               text-slate-400 hover:text-slate-900 transition">
        Back
      </button>
      <div  v-if="step<=1"></div>
      <button @click="saveDraft" class="bg-primary text-white px-12 py-4 rounded-xl
               font-bold text-xs uppercase tracking-wider
               hover:opacity-90 transition">
        {{step>=3?"Launch Campaign":"Save & Continue"}}
      </button>
    </section>

  </section>
</template>

<script>
  import { createCampaignApi } from "../apis/agencampapis";
  import { loadShortlistApi } from "../apis/shortapis";

  import { useToast } from "vue-toastification";
  import "vue-toastification/dist/index.css";

  export default {
    name: "AgencyCampaignsNew",

    data() {
      return {
        step: 1,
        activeStep: "text-slate-900",
        inactiveStep: "text-slate-300",
        user_id: "",
        toast: null,
        form: {
          name: "",
          objective: "",
          description: "",
          startDate: "",
          endDate: "",
          interests: "",
          education: "",
          region: ""
        },
        influencers: [],
        selectedInfluencers:[],
        loading: false
      };
    },

    mounted() {
      this.toast = useToast();
      try {

        const ldataRaw = sessionStorage.getItem("ldata");

        if (ldataRaw && ldataRaw !== "undefined") {

          const ldata = JSON.parse(ldataRaw);

          if (ldata && ldata.user_id) {
            this.user_id = ldata.user_id;
            this.loadShortlist();
          }
        }

      } catch (e) {

        console.log("Session parse error", e);
      }
    },
    computed:{
       totalReach() {

        if (!this.selectedInfluencers.length) return "0";

        const total = this.selectedInfluencers.reduce((sum, inf) => {

          const fans = parseInt(
            (inf.fans || "0").toString().replace(/[^0-9]/g, "")
          );

          return sum + fans;

        }, 0);

        if (total > 1000000) return (total / 1000000).toFixed(1) + "M";
        if (total > 1000) return (total / 1000).toFixed(0) + "K";

        return total;
      }
    },
    methods: {
      toggleShortlist(inf) {

        if (!inf) return;

        const index = this.selectedInfluencers.findIndex(
          i => i.infId === inf.infId
        );

        // ✅ Already selected → REMOVE
        if (index !== -1) {

          this.selectedInfluencers.splice(index, 1);

          inf.shortlisted = "no";

          return;
        }

        // ✅ Not selected → ADD
        this.selectedInfluencers.push({
          infId: inf.infId,
          name: inf.name,
          platform: inf.platform,
          category: inf.category,
          followers: inf.fans,
          fitScore: inf.fitScore || 0
        });

        inf.shortlisted = "yes";
      },
    async loadShortlist() {

        const res = await loadShortlistApi({
          agency_id: this.user_id,
          campaign_id: ""
        });

        if (res && res.status) {

          if (res.records && res.records.length) {
            this.influencers = res.records.map(r => ({
              id: r.shortlist_id,
              infId: r.shortlist_influencer || "",
              name: r.shortlist_inf_name || "Unknown Creator",
              platform: r.shortlist_inf_platform || "-",
              category: r.shortlist_inf_category || "-",
              followers: r.shortlist_inf_fans || "0",
              fitScore: r.fitScore || 0,
              trustScore: r.trustScore || 0,
              riskScore: r.riskScore || 0
            }));
          } else {
            this.influencers = [];
          }

        } else {

          console.log("Shortlist API failed");
          this.influencers = [];
        }
      },
      validateForm() {

        if (!this.form.name) {
          this.toast.error("Campaign name required");
          return false;
        }

        if (!this.form.objective) {
          this.toast.error("Campaign objective required");
          return false;
        }

        if (!this.form.startDate) {
          this.toast.error("Start date required");
          return false;
        }

        if (!this.form.endDate) {
          this.toast.error("End date required");
          return false;
        }

        if (this.form.startDate > this.form.endDate) {
          this.toast.error("Invalid campaign timeline");
          return false;
        }

        return true;
      },
      backNow(){
        if(this.step>1){
          this.step--;
        }
      },
      async saveDraft() {

        if (this.loading) return;

        // ✅ STEP 1 VALIDATION
        if (this.step === 1) {

          if (!this.validateForm()) return;

          this.step = 2;
          return;
        }

        // ✅ STEP 2 VALIDATION (CRITICAL BUSINESS RULE)
        if (this.step === 2) {

          if (this.selectedInfluencers.length === 0) {

            this.toast.error("Select at least one influencer");
            return;
          }

          this.step = 3;
          return;
        }

        // ✅ STEP 3 → SAVE DRAFT
        if (this.step === 3) {

          await this.persistCampaign("draft");
        }
      },
      async persistCampaign(status) {

        this.loading = true;

        const payload = {

          campaign_user: this.user_id,
          campaign_title: this.form.name,
          campaign_desc: this.form.description,
          campaign_type: this.form.objective,

          campaign_start_date: this.form.startDate,
          campaign_end_date: this.form.endDate,

          campaign_target_audience: JSON.stringify({
            interests: this.form.interests,
            education: this.form.education
          }),

          campaign_target_regions: this.form.region,

          campaign_status: status,

          influencers: JSON.stringify(this.selectedInfluencers),  // ⭐ IMPORTANT

          by: this.user_id
        };

        const res = await createCampaignApi(payload);

        this.loading = false;

        if (res.status) {

          if (status === "active") {
            this.toast.success("Campaign launched");
          } else {
            this.toast.success("Campaign saved as draft");
          }

          setTimeout(() => {
            window.location.href = "/agency/campaigns";
          }, 800);

        } else {

          this.toast.error(res.success || "Campaign failed");
        }
      },
      async launchCampaign() {

        if (this.loading) return;

        if (this.selectedInfluencers.length === 0) {

          this.toast.error("No influencers selected");
          return;
        }

        await this.persistCampaign("active");
      }
    }
  };
</script>


<style scoped>
  .label {
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    color: #94a3b8;
  }

  .input {
    width: 100%;
    margin-top: 6px;
    padding: 14px 16px;
    border-radius: 1rem;
    background: #f8fafc;
    font-weight: 600;
  }

  /* ===================================
   NEW CAMPAIGN – MOBILE PATCH
   =================================== */
  @media (max-width: 640px) {

    /* Remove heavy horizontal padding */
    .px-12 {
      padding-left: 16px !important;
      padding-right: 16px !important;
    }

    .py-12 {
      padding-top: 24px !important;
      padding-bottom: 32px !important;
    }

    /* Header */
    header h2 {
      font-size: 26px !important;
      line-height: 1.2;
    }

    header p {
      font-size: 14px;
    }

    /* Step indicator – allow wrap */
    section.flex.items-center.gap-6 {
      flex-wrap: wrap;
      gap: 8px;
      font-size: 10px;
    }

    section.flex.items-center.gap-6 span {
      white-space: nowrap;
    }

    /* Main form card */
    .rounded-\\[2\\.5rem\\] {
      padding: 20px !important;
      border-radius: 20px !important;
    }

    /* Grid spacing */
    .gap-10 {
      gap: 24px !important;
    }

    /* Two-column dates → stack */
    .md\\:grid-cols-2 {
      grid-template-columns: 1fr !important;
    }

    /* Inputs & textarea */
    .input {
      padding: 12px 14px;
      font-size: 14px;
      border-radius: 12px;
    }

    textarea.input {
      min-height: 120px;
    }

    /* Right column note */
    .bg-slate-50.p-6 {
      padding: 16px !important;
      font-size: 11px;
      line-height: 1.5;
    }

    /* Actions */
    section.flex.justify-between.items-center {
      flex-direction: column-reverse;
      gap: 16px;
      align-items: stretch;
    }

    section.flex.justify-between.items-center button {
      width: 100%;
      text-align: center;
    }

    /* Primary CTA */
    .px-12.py-4 {
      padding: 14px 16px !important;
      font-size: 11px;
    }
  }

  .ghost-btn {
    border: 1px solid #e2e8f0;
    padding: 10px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    background: white;
  }

  .ghost-btn:hover {
    background: #f8fafc;
  }
</style>